import * as React from "react";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import ProviderAffiliationsAccordionResults from "@app/projects/servicing-provider/ProviderAffiliationsAccordion";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { EntityTypeEnum, NetworkAssessmentAnalysisType, LocationConfidenceScoresLabelEnum, NPIStatus } from "@app/projects/enumerators";
import ProjectOptions from "@app/projects/projectOptions";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import ProjectService from "@app/projects/projectService";
import ProviderAffiliationService from "@app/projects/providerAffiliationsService";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { CareJourneyScores } from "@app/projects/servicing-provider/CareJourneyScores";
import ClaimsProviderInsights from "@app/projects/servicing-provider/ClaimsProviderInsights";
import CrossCountyResults from "@app/projects/servicing-provider/CrossCountyResults";
import MotiveScores from "@app/projects/servicing-provider/MotiveScores";
import { QuestProviderDataInsights } from "@app/projects/servicing-provider/QuestProviderDataInsights";
import { getEntityIconClasses, getEntityTypeDescription } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import formatPhoneNumber from "@app/shared/formatPhoneNumber";
import InfoIndicator from "@app/shared/InfoIndicator";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { valueOrDashes } from "@app/shared/utility/valueOrDashes";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider/servicingProviderDetail";

interface ServicingProviderProfileProps {
  isProjected?: boolean;
  isTerminated?: boolean;
  isZipProject?: boolean;
  netNewProvider?: boolean;
  nextClosestProvider?: boolean;
  project: ProjectOptions;
  provider: ServicingProvider;
  regionSetId: number;
  runConfiguration: ProjectRunConfigurationResult;
  providerNetworkType: ProviderNetworkTypeEnum;
  specialtyGroupCode: string;
}

interface ServicingProviderProfileState {
  record: ServicingProviderDetail;
}

let entityType = EntityTypeEnum.Unknown;

export default class ServicingProviderProfile extends React.Component<ServicingProviderProfileProps, ServicingProviderProfileState> {
  static contextType = FeatureFlagContext;
  declare context: React.ContextType<typeof FeatureFlagContext>;

  private readonly projectService = new ProjectService();

  constructor(props: ServicingProviderProfileProps) {
    super(props);
    this.state = {
      record: null,
    };
  }

  public async componentDidMount() {
    const record = await this.projectService.servicingProviderDetail(
      this.props.project.projectId,
      this.props.regionSetId,
      this.props.runConfiguration.projectRunConfigurationId,
      this.props.specialtyGroupCode,
      this.props.providerNetworkType,
      this.props.provider.providerId,
      this.props.runConfiguration.networkAssessmentAnalysisType || NetworkAssessmentAnalysisType.ServiceArea,
      this.props.nextClosestProvider,
      this.props.netNewProvider);

    // If we fail to get anything back from the API, we can at least display what we already have and save face.
    this.setState((prevState) => ({
      ...prevState,
      record: record ? record : this.props.provider as unknown as ServicingProviderDetail,
    }));
  }

  public render() {
    const record = this.state && this.state.record
      ? this.state.record
      : null;

    return (
      <>
        {(!record || null) && (this.renderLoading())}
        {(record || null) && (this.renderProfile(record))}
      </>
    );
  }
  private renderLoading() {
    const providerAffiliationService = new ProviderAffiliationService();
    if (this.props.provider.npi != null) {
      providerAffiliationService.GetNPPESEntityTypeByNPI(this.props.provider.npi).then((data) => {
        entityType = data;
      });
    };

    return (
      <div className="row">
        <div className="col-12 text-center">
          <span>
            <i className="fa fa-spinner fa-spin" /> Loading additional information for <strong>{this.props.provider.displayName}</strong>...
          </span>
        </div>
      </div>
    );
  }
  private renderProfile(record: ServicingProviderDetail) {
    const showAttestation = this.props.providerNetworkType !== ProviderNetworkTypeEnum.Market && this.props.project.showAttestation;
    const showConfidenceScore = this.props.runConfiguration.showLocationConfidenceScores;
    const isZipNextClosestProvider = this.props.isZipProject && this.props.nextClosestProvider;
    const showPgrConfidenceTier = this.props.runConfiguration.showPgrConfidenceTier
      && ((this.props.providerNetworkType === ProviderNetworkTypeEnum.Market && !isZipNextClosestProvider)
        || this.context.enabledFeatureFlags.has(Features.SHOW_CONFIDENCE_TIER_NON_MARKET_ACCESS_POINTS));

    return (
      <div className="servicing-provider-profile">
        <section className="intro">
          <div className="row">
            <div className="col-sm-3 mb-3">
              <i className={entityType != EntityTypeEnum.Unknown ? getEntityIconClasses(entityType) : getEntityIconClasses(record.entityType)} />
            </div>

            <div className="col-sm-9 mb-3">
              <h1 className="pl0-imp">
                {record.displayName}
              </h1>
              {entityType != EntityTypeEnum.Unknown ?
                <div>
                  <strong className="text-muted">{getEntityTypeDescription(entityType)}</strong>
                  <br />
                </div> :
                record.entityType ?
                  <div>
                    <strong className="text-muted">{getEntityTypeDescription(record.entityType)}</strong>
                    <br />
                  </div> :
                  null
              }
              {record.address ? <>{record.address}<br /></> : null}
              {record.address2 ? <>{record.address2}<br /></> : null}
              {record.city}
              {record.city && (record.state || record.zip) ? ", " : null}
              {record.state} {record.zip}<br />
              <strong>NPI Status</strong> {record.npiStatus ? this.renderStatus(record.npiStatus) : null}
              {showConfidenceScore && <><strong className="pl5">Practitioner at Location Confidence Score</strong> {this.renderLocationConfidence(record.locationConfidenceScoresDisplay)}</>}
              {this.renderAttestationStatus(showAttestation, record.lastAttestedDate)}
            </div>
          </div>
        </section>

        <AccordionContainer>
          <Accordion title="General" defaultOpen={true} testId="servicing-provider-profile__general-accordion">
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">NPI</div>
              <div className="data-box-content">
                {valueOrDashes(record.npi)}
              </div>
            </div>
            <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
              <div className="data-box col-12 col-sm-6 col-md-4">
                <div className="data-box-header">TIN</div>
                <div className="data-box-content">
                  {valueOrDashes(record.tin)}
                </div>
              </div>
            </RoleRestricted>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Prefix</div>
              <div className="data-box-content">
                {valueOrDashes(record.prefix)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">First</div>
              <div className="data-box-content">
                {valueOrDashes(record.firstName)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldNames === "FirstName").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldNames === "FirstName").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Middle</div>
              <div className="data-box-content">
                {valueOrDashes(record.middleName)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "MiddleName").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "MiddleName").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Last</div>
              <div className="data-box-content">
                {valueOrDashes(record.lastName)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "LastName").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "LastName").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Suffix</div>
              <div className="data-box-content">
                {valueOrDashes(record.suffix)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Phone</div>
              <div className="data-box-content">
                {valueOrDashes(formatPhoneNumber(record.phone))}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "Phone").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "Phone").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Fax</div>
              <div className="data-box-content">
                {valueOrDashes(formatPhoneNumber(record.fax))}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Taxonomy</div>
              <div className="data-box-content">
                {valueOrDashes(record.taxonomyCode)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Specialty Code{record.specialtyCodes.length > 1 ? "s" : ""}</div>
              <div className="data-box-content">
                {valueOrDashes(record.specialtyCodes.join(", "))}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Specialt{record.specialties.length > 1 ? "ies" : "y"}</div>
              <div className="data-box-content">
                {valueOrDashes(record.specialties.join(", "))}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Credentials</div>
              <div className="data-box-content">
                {valueOrDashes(record.credentials)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Gender</div>
              <div className="data-box-content">
                {valueOrDashes(record.gender)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Name</div>
              <div className="data-box-content">
                {valueOrDashes(record.organizationName)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Organization NPI</div>
              <div className="data-box-content">
                {valueOrDashes(record.organizationNPI)}
              </div>
            </div>
          </Accordion>
          {showPgrConfidenceTier &&
            <Accordion title="Access Point Confidence" testId="servicing-provider-profile__pgr-confidence-accordion">
              <QuestProviderDataInsights record={record} />
            </Accordion>
          }
          <Accordion title="Address / Location" testId="servicing-provider-profile__address-location-accordion">
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Address</div>
              <div className="data-box-content">
                {valueOrDashes(record.address)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "Address").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "Address").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Address 2</div>
              <div className="data-box-content">
                {valueOrDashes(record.address2)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "Address2").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "Address2").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4 p-0">
              <div className="data-box col-6">
                <div className="data-box-header">County SSA</div>
                <div className="data-box-content">
                  {valueOrDashes(record.countySSA)}
                </div>
              </div>
              <div className="data-box col-6">
                <div className="data-box-header">County</div>
                <div className="data-box-content">
                  {record.countyName}
                </div>
              </div>
            </div>
            {this.context.enabledFeatureFlags.has(Features.SSA_TO_FIPS) &&
              <>
                <div className="data-box col-12 col-sm-6 col-md-4">
                  <div className="data-box-header">County FIPS</div>
                  <div className="data-box-content">
                    {valueOrDashes(record.countyFIPS)}
                  </div>
                </div>
                <div className="data-box col-12 col-sm-6 col-md-4">
                  <div className="data-box-header">Planning Region</div>
                  <div className="data-box-content">
                    {valueOrDashes(record.planningRegion)}
                  </div>
                </div>
              </>
            }
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">City</div>
              <div className="data-box-content">
                {valueOrDashes(record.city)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "City").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "City").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">State</div>
              <div className="data-box-content">
                {valueOrDashes(record.state)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "State").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "State").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Zip</div>
              <div className="data-box-content">
                {valueOrDashes(record.zip)}
                {/*(this.props.provider.accuracyResults && this.props.provider.accuracyResults.length > 0) ?
                  <>
                    <span className={`ml10 label label-${this.props.provider.accuracyResults.find((r) => r.fieldName === "Zip").resultEvaluation}`}>
                      {this.props.provider.accuracyResults.find((r) => r.fieldName === "Zip").resultEvaluation}
                    </span>
                  </>
                  : null
                */}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Latitude</div>
              <div className="data-box-content">
                {valueOrDashes(record.latitude)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">Longitude</div>
              <div className="data-box-content">
                {valueOrDashes(record.longitude)}
              </div>
            </div>
            <div className={`data-box col-12 col-sm-6 col-md-4 ${showConfidenceScore && "p-0"}`}>
              {showConfidenceScore ?
                <>
                  <div className="data-box col-6">
                    <div className="data-box-header">GeoInfo</div>
                    <div className="data-box-content">
                      {record.geoInfoDisplay}
                    </div>
                  </div>
                  <div className="data-box col-6">
                    <div className="data-box-header">Practitioner at Location Confidence Score</div>
                    <div className="data-box-content">
                      {record.locationConfidenceScoresDisplay ?? LocationConfidenceScoresLabelEnum.Unknown}
                    </div>
                  </div>
                </> :
                <>
                  <div className="data-box-header">GeoInfo</div>
                  <div className="data-box-content">
                    {record.geoInfoDisplay}
                  </div>
                </>
              }
            </div>

          </Accordion>
          <Accordion title="Claims" testId="servicing-provider-profile__claims-accordion">
            {
              this.props.project.showPciData ? (
                <ClaimsProviderInsights showTricare={this.props.project.showTricareClaimsVolumeColumn} showVA={this.props.project.showVaClaimsVolumeColumn} record={record} />
              ) : (
                <QESAlert classNames="text-transform-none mb-0" type="info" persist={true}>
                  Quest can deliver powerful provider performance insights through the <b>Provider Claims Insights</b> package. Please contact your sales representative. <br /> <br />
                  Insights include: <br />
                  <ul className="mb-0">
                    <li>
                      Provider effectiveness (quality) and efficiency (cost) scores
                    </li>
                    <li>
                      Provider activity by Line of Business
                    </li>
                    <li>
                      Provider payer relationships
                    </li>
                    <li>
                      Provider top conditions and procedures
                    </li>
                  </ul>
                </QESAlert>
              )}
          </Accordion>
          {this.props.project.showMotiveAppropriatenessScore && (
            <Accordion testId="servicing-provider-profile__motive-accordion" title={(
              <>
                <span className="right-padding">Motive Appropriateness Tier</span>
                <img src={`${window.customUIBaseUrl}/images/motive-h-color.svg`} height="21" />
              </>
            )}>
              <MotiveScores record={record} />
            </Accordion>
          )}
          {this.props.project.showCareJourneyScores && (
            <Accordion testId="servicing-provider-profile__carejourney-accordion" title={(
              <>
                <span className="right-padding">CareJourney Provider Performance</span>
                <img src={`${window.customUIBaseUrl}/images/carejourney-logo.svg`} height="21" />
              </>
            )}>
              <CareJourneyScores record={record} />
            </Accordion>
          )}
          {(this.props.project.showMipsScore && this.props.runConfiguration?.showMipsScore) && (
            <RoleRestricted allowedRoles={[Roles.QUALITY_SCORES]}>
              <Accordion title="Quality" testId="servicing-provider-profile__quality-accordion">
                {
                  <>
                    {this.renderMipsScores(record)}
                  </>
                  || <>No Quality Scores project options are on.</>
                }
              </Accordion>
            </RoleRestricted>
          )}
          {!this.props.isZipProject ? (
            <Accordion
              title="Cross-County Results"
              testId="servicing-provider-profile__cross-county-results-accordion"
              lazyLoad={true}
            >
              <CrossCountyResults
                records={record.crossCountyResults}
                showGainsAndImpact={this.props.providerNetworkType === ProviderNetworkTypeEnum.Prospect || this.props.providerNetworkType === ProviderNetworkTypeEnum.Market} />
            </Accordion>
          ) : null}
          <Accordion title="Miscellaneous" testId="servicing-provider-profile__miscellaneous-accordion">
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Accepting New Patients</div>
              <div className="data-box-content">
                {valueOrDashes(record.acceptingNewPatients)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Quality</div>
              <div className="data-box-content">
                {valueOrDashes(record.employmentStatus)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Under Contract</div>
              <div className="data-box-content">
                {valueOrDashes(record.underContract)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Contract Type</div>
              <div className="data-box-content">
                {valueOrDashes(record.contractType)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Medicare CCN</div>
              <div className="data-box-content">
                {valueOrDashes(record.medicareCCN)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Medicare Certified Beds</div>
              <div className="data-box-content">
                {valueOrDashes(record.medicareCertifiedBeds)}
              </div>
            </div>
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Accepts Medicare Assignment</div>
              <div className="data-box-content">
                {valueOrDashes(record.acceptsMedicareAssignment)}
              </div>
            </div>
            {!this.context.enabledFeatureFlags.has(Features.REMOVAL_MEDGROUP_AFFILIATION) &&
              <>
                <div className="data-box col-12 col-sm-6">
                  <div className="data-box-header">Med Group Affiliation</div>
                  <div className="data-box-content">
                    {valueOrDashes(record.medGroupAffiliation)}
                  </div>
                </div>
              </>}
            <div className="data-box col-12 col-sm-6">
              <div className="data-box-header">Letter of Intent</div>
              <div className="data-box-content">
                {valueOrDashes(record.letterOfIntent)}
              </div>
            </div>
            <RoleRestricted allowedRoles={[Roles.MEDICARE_OPT_OUTS]} deniedRoles={[Roles.TOKEN_USER]}>
              <div className="data-box col-12 col-sm-6">
                <div className="data-box-header">Medicare Opt-Out</div>
                <div className="data-box-content">
                  {valueOrDashes(record.medicareOptOut)}
                </div>
              </div>
            </RoleRestricted>
            <RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}>
              <div className="data-box col-12 col-sm-6">
                <div className="data-box-header">OIG Excluded</div>
                <div className="data-box-content">
                  {valueOrDashes(record.oigExcluded)}
                </div>
              </div>
              <div className="data-box col-12 col-sm-6">
                <div className="data-box-header">OIG Exclusion Waiver</div>
                <div className="data-box-content">
                  {valueOrDashes(record.oigWaiver)}
                </div>
              </div>
            </RoleRestricted>
            <RoleRestricted allowedRoles={[Roles.PRECLUDED_PROVIDER_USER]} deniedRoles={[Roles.TOKEN_USER]}>
              <div className="data-box col-12 col-sm-6">
                <div className="data-box-header">Medicare Precluded</div>
                <div className="data-box-content">
                  {valueOrDashes(record.precluded)}
                </div>
              </div>
            </RoleRestricted>
            {this.props.project.showNppesDates && this.props.runConfiguration && this.props.runConfiguration.showNppesDates &&
              <>
                <div className="data-box col-12 col-sm-6">
                  <div className="data-box-header">NPPES Certification Date</div>
                  <div className="data-box-content">
                    {this.renderUnconvertedDate(record.nppesCertificationDate)}
                  </div>
                </div>
                <div className="data-box col-12 col-sm-6">
                  <div className="data-box-header">NPPES Last Updated Date</div>
                  <div className="data-box-content">
                    {this.renderUnconvertedDate(record.nppesLastUpdatedDate)}
                  </div>
                </div>
              </>
            }
          </Accordion>

          {this.props.project.showAffiliationsInsights &&
            <>
              <Accordion title="Affiliations" testId="servicing-provider-profile__affiliation-accordion">
                <ProviderAffiliationsAccordionResults
                  provider={record}
                  providerAffiliationVersionId={this.props.runConfiguration.providerAffiliationVersionId ?? 0}
                  entityType={entityType}
                ></ProviderAffiliationsAccordionResults>
              </Accordion>
            </>
          }

          {this.props.isProjected && (
            <Accordion title="Notes" testId="servicing-provider-profile__notes-accordion">
              {(this.props.provider as ProjectionProvider).reasonDescription || "No reason given."}
            </Accordion>
          )}

          {this.props.isTerminated && (
            <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN, Roles.TERM_PROVIDER_VIEWER]}>
              <Accordion title="Notes" testId="servicing-provider-profile__notes-accordion">
                {(this.props.provider as NetworkServicingProviderTerminationModel).reasonDescription || "No reason given."}
              </Accordion>
            </RoleRestricted>
          )}
        </AccordionContainer>

        {showAttestation &&
          <AccordionContainer>
            <Accordion title="Attestation" testId="servicing-provider-profile__attestation-accordion">
              <div className="data-box col-xs-12 col-sm-6 col-md-4">
                <div className="data-box-header">Is Attested</div>
                <div className="data-box-content">
                  {valueOrDashes(record.isRecentlyAttested)}
                </div>
              </div>
              <div className="data-box col-xs-12 col-sm-6 col-md-4">
                <div className="data-box-header">Days Since Last Attested</div>
                <div className="data-box-content">
                  {valueOrDashes(record.daysSinceLastAttested)}
                </div>
              </div>
              <div className="data-box col-xs-12 col-sm-6 col-md-4">
                <div className="data-box-header">Last Attested Date</div>
                <div className="data-box-content">
                  {record.lastAttestedDate ? formatUnconvertedDateCustom(record.lastAttestedDate, "L") : "---"}
                </div>
              </div>
            </Accordion>
          </AccordionContainer>
        }
      </div>
    );
  }

  //returns a date as is, without converting it to user's timezone
  private renderUnconvertedDate(unparsedDate: Date) {
    if (!unparsedDate) {
      return "---";
    }

    return formatUnconvertedDateCustom(unparsedDate, "YYYY-MM-DD");
  }

  private renderStatus(npiStatus: NPIStatus) {
    switch (npiStatus) {
      case NPIStatus.Deactivated:
      case NPIStatus.Invalid:
        return (<small className="text-danger pl5"><i className="fa fa-times supress-panel-styles" /> {npiStatus}</small>);
      case NPIStatus.Missing:
      case NPIStatus.Unknown:
        return (<small className="text-warning pl5"><i className="fa fa-question supress-panel-styles" /> {npiStatus}</small>);
      case NPIStatus.Valid:
        return (<small className="text-success pl5"><i className="fa fa-check supress-panel-styles" /> {npiStatus}</small>);
    }
  }

  private renderLocationConfidence(confidence: string) {
    switch (confidence?.replace(' ', '')) {
      case LocationConfidenceScoresLabelEnum.VeryLow:
        return (<small className="text-danger pl5"> {confidence}</small>);
      case LocationConfidenceScoresLabelEnum.Low:
        return (<small className="text-warning pl5"> {confidence}</small>)
      case LocationConfidenceScoresLabelEnum.Medium:
      case LocationConfidenceScoresLabelEnum.High:
        return (<small className="text-success pl5"> {confidence}</small>)
      default:
        return (<small className="text-secondary pl5"> {LocationConfidenceScoresLabelEnum.Unknown}</small>);
    }
  }

  private renderAttestationStatus(showAttestation: boolean, lastAttestedDate?: Date) {
    if (!showAttestation) {
      return;
    }

    return (
      <>
        <br />
        <strong>Last Attested Date</strong> {lastAttestedDate ? formatUnconvertedDateCustom(lastAttestedDate, "L") : "---"}
      </>
    );

  }

  private renderMipsScores(record: ServicingProviderDetail) {
    return (
      <div className="data-box col-12 col-sm-6">
        <div className="data-box-header">
          MIPS Score
          <span>&nbsp;</span>
          <InfoIndicator className={"informational-indicator"}
            tooltipTitle={
              <div style={{ textAlign: "left" }}>
                <div><strong>MIPS Quality Score</strong></div>
                <div>It<span>&#8217;</span>s possible to participate in MIPS multiple ways. If a clinician (identified by a single unique TIN/NPI combination) has more than one MIPS final score, here<span>&#8217;</span>s how CMS determines which final score is received:</div>
                <ul>
                  <li>If the NPI is reflective of participation as a virtual group, the NPI will receive the virtual group<span>&#8217;</span>s final score, even if there are additional final scores form other participation options.</li>
                  <li>If the NPI is reflective of participation as the individual, group, and/or an APM Entity, the NPI will receive the highest available score.</li>
                </ul>
              </div>
            }
            tooltipPlacement={"right"} />
        </div>
        <div className="data-box-content">
          {valueOrDashes(record.mipsScore, false)}
        </div>
      </div>
    );
  }
}